/**
 * Product Navigation Bar
 * Handles sticky navigation and smooth scrolling functionality
 * Uses IntersectionObserver for reliable sticky trigger.
 */

class ProductNavBar {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.staticNavBar = section.querySelector(`#static-nav-${this.sectionId}`);
    this.stickyNavBar = section.querySelector(`#sticky-nav-${this.sectionId}`);
    this.navLinks = section.querySelectorAll('.product-nav-bar__link');
    this.enableSticky = section.dataset.enableSticky === 'true';
    this.observerTrigger = section.querySelector(`#nav-bar-trigger-${this.sectionId}`);
    this.observer = null;

    // Quantity inputs and thumbnails
    this.staticQty = section.querySelector(`#NavQuantity-static-${this.sectionId}`);
    this.stickyQty = section.querySelector(`#NavQuantity-sticky-${this.sectionId}`);
    this.mainQty =
      document.querySelector(`#Quantity-${this.sectionId}-row`) ||
      document.querySelector('product-form.main-product-form input[name="quantity"]') ||
      document.querySelector('form[action*="/cart/add"] input[name="quantity"]');
    this.staticThumb = section.querySelector(`#nav-thumb-static-${this.sectionId}`);
    this.stickyThumb = section.querySelector(`#nav-thumb-sticky-${this.sectionId}`);
    this.staticSummary = section.querySelector(`#nav-summary-static-${this.sectionId}`);
    this.stickySummary = section.querySelector(`#nav-summary-sticky-${this.sectionId}`);

    this.init();
  }

  init() {
    if (!this.staticNavBar || !this.stickyNavBar) return;

    this.staticNavBar.classList.remove('hide');
    this.stickyNavBar.classList.remove('show');

    if (this.enableSticky) {
      this.initStickyWatcher();
    }

    this.navLinks.forEach((link) => {
      link.addEventListener('click', this.handleNavClick.bind(this));
    });

    const ctas = this.section.querySelectorAll('.product-nav-bar__cta [data-add-to-cart="true"]');
    ctas.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const qty = this.getActiveQtyValue();
        if (qty && this.mainQty) {
          this.mainQty.value = qty;
          this.mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.handleCtaClick(e);
      });
    });

    this.initScrollSpy();
    this.initVariantListener();
    this.bindQtySync();
  }

  initStickyWatcher() {
    this.ticking = false;
    this.computeStaticTop();
    this.updateStickyState();

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateStickyState();
      });
    };

    this.onResize = () => {
      this.computeStaticTop();
      this.updateStickyState();
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    window.addEventListener('load', this.onResize);
  }

  getHeaderHeight() {
    const selectors = ['sticky-header', '.header-wrapper', '#shopify-section-header', 'header'];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el.offsetHeight || parseInt(getComputedStyle(el).height, 10) || 0;
    }
    return 0;
  }

  computeStaticTop() {
    if (!this.staticNavBar) return;
    const rect = this.staticNavBar.getBoundingClientRect();
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.staticTop = rect.top + scrollY;
    this.staticHeight = this.staticNavBar.offsetHeight || rect.height || 0;
  }

  updateStickyState() {
    const headerHeight = this.getHeaderHeight();
    const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (this.staticTop == null) this.computeStaticTop();

    // Position sticky nav below header
    this.stickyNavBar.style.top = `${headerHeight}px`;

    // Show sticky when the static nav’s top goes under the header
    const triggerY = (this.staticTop || 0) - headerHeight;

    if (currentY >= triggerY) {
      this.stickyNavBar.classList.add('show');
      this.staticNavBar.classList.add('hide');
    } else {
      this.stickyNavBar.classList.remove('show');
      this.staticNavBar.classList.remove('hide');
    }
  }

  handleNavClick(e) {
    const link = e.target.closest('.product-nav-bar__link');
    if (!link) return;

    const targetSelector = link.getAttribute('href');

    // Skip if it's a void link
    if (!targetSelector || targetSelector === 'javascript:void(0);') {
      e.preventDefault();
      return;
    }

    // Skip if it's an external link or anchor
    if (targetSelector.startsWith('http') || !targetSelector.startsWith('#')) {
      return;
    }

    e.preventDefault();

    // Remove active class from all links
    this.section.querySelectorAll('.product-nav-bar__link').forEach((btn) => {
      btn.classList.remove('is-active');
    });

    // Add active class to clicked link and its counterpart
    const linkText = link.textContent.trim();
    this.section.querySelectorAll('.product-nav-bar__link').forEach((btn) => {
      if (btn.textContent.trim() === linkText) {
        btn.classList.add('is-active');
      }
    });

    // Scroll to target
    this.scrollToTarget(targetSelector);
  }

  scrollToTarget(targetSelector) {
    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    const headerHeight = this.getHeaderHeight();
    const navBarHeight = this.stickyNavBar.offsetHeight || this.staticNavBar.offsetHeight;
    const offset = headerHeight + navBarHeight + 10;

    const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });
  }

  handleCtaClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const mainForm = document.querySelector('product-form.main-product-form form[data-type="add-to-cart-form"]')
      || document.querySelector('form[data-type="add-to-cart-form"]');
    if (!mainForm) return;
    const submitButton = mainForm.querySelector('button[type="submit"]');
    if (submitButton && submitButton.disabled !== true && submitButton.getAttribute('aria-disabled') !== 'true') {
      submitButton.click();
      return;
    }
    if (typeof mainForm.requestSubmit === 'function') {
      mainForm.requestSubmit();
    } else {
      mainForm.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  }

  initScrollSpy() {
    // Get all target sections
    const targetSections = [];
    this.navLinks.forEach((link) => {
      const targetSelector = link.getAttribute('href');
      if (targetSelector && targetSelector.startsWith('#')) {
        const targetElement = document.querySelector(targetSelector);
        if (targetElement) {
          targetSections.push({
            element: targetElement,
            id: targetSelector,
            linkText: link.textContent.trim(),
          });
        }
      }
    });

    if (targetSections.length === 0) return;

    // Observe scroll position and update active state
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const activeSection = targetSections.find((section) => section.element === entry.target);
          if (activeSection) {
            // Remove active class from all links
            this.section.querySelectorAll('.product-nav-bar__link').forEach((link) => {
              link.classList.remove('is-active');
            });

            // Add active class to corresponding links
            this.section.querySelectorAll('.product-nav-bar__link').forEach((link) => {
              if (link.textContent.trim() === activeSection.linkText) {
                link.classList.add('is-active');
              }
            });
          }
        }
      });
    }, observerOptions);

    // Observe all target sections
    targetSections.forEach((section) => {
      observer.observe(section.element);
    });
  }

  initVariantListener() {
    // Listen for variant changes to update button text and UI
    const productForm = document.querySelector('product-form.main-product-form');
    if (!productForm) return;

    // Check for pre-order flag on page load
    this.updateCtaButtonText();

    // Listen for variant change events
    document.addEventListener('variant:change', (e) => {
      setTimeout(() => {
        this.updateCtaButtonText();
      }, 100);
      const variant = e && e.detail && e.detail.variant ? e.detail.variant : null;
      if (variant) {
        // Update thumbnail
        const src =
          (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) ||
          (variant.featured_image && variant.featured_image.src) ||
          '';
        if (src) {
          const url = this.sizeImage(src, 80);
          if (this.staticThumb) this.staticThumb.src = url;
          if (this.stickyThumb) this.stickyThumb.src = url;
        }
        // Update summary text
        const money = this.formatMoney(variant.price);
        const text = `${variant.title} — ${money}`;
        if (this.staticSummary) this.staticSummary.textContent = text;
        if (this.stickySummary) this.stickySummary.textContent = text;
        // Update quantity rules
        const min = (variant.quantity_rule && variant.quantity_rule.min) || 1;
        const max = variant.quantity_rule ? variant.quantity_rule.max : null;
        const step = (variant.quantity_rule && variant.quantity_rule.increment) || 1;
        [this.staticQty, this.stickyQty].forEach((el) => {
          if (!el) return;
          el.min = min;
          el.setAttribute('data-min', String(min));
          el.step = step;
          if (max != null) {
            el.max = max;
            el.setAttribute('data-max', String(max));
          } else {
            el.removeAttribute('max');
            el.removeAttribute('data-max');
          }
          if (parseInt(el.value || '0', 10) < min) {
            el.value = min;
          }
        });
      }
    });
  }

  updateCtaButtonText() {
    const productForm = document.querySelector('product-form.main-product-form form[data-type="add-to-cart-form"]');
    if (!productForm) return;

    const preorderFlag = productForm.querySelector('.is-preorder-product');
    const navBarPreorderFlag = this.section.querySelector('.nav-bar-preorder-flag');
    
    const ctaLabels = this.section.querySelectorAll('.nav-bar-cta-label');
    const defaultText = this.section.dataset.isPreorder === 'true' ? 
      this.section.dataset.preorderText : 
      ctaLabels[0]?.textContent.trim();

    ctaLabels.forEach((label) => {
      if (preorderFlag || navBarPreorderFlag) {
        const preorderText = preorderFlag?.dataset.preorderText || 
                           navBarPreorderFlag?.dataset.preorderText || 
                           'Pre-order Now';
        label.textContent = preorderText;
      } else if (defaultText && !preorderFlag && !navBarPreorderFlag) {
        // Reset to default if no pre-order
        label.textContent = defaultText;
      }
    });
  }

  // Sync quantity values between static, sticky and main form
  bindQtySync() {
    const link = (source, peers = []) => {
      if (!source) return;
      const propagate = (val) => {
        peers.forEach((p) => {
          if (p) {
            p.value = val;
            p.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        if (this.mainQty) {
          this.mainQty.value = val;
          this.mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      source.addEventListener('input', () => propagate(source.value));
      source.addEventListener('change', () => propagate(source.value));
    };
    link(this.staticQty, [this.stickyQty]);
    link(this.stickyQty, [this.staticQty]);
  }

  getActiveQtyValue() {
    const stickyVisible = this.stickyNavBar && this.stickyNavBar.classList.contains('show');
    const input = stickyVisible ? this.stickyQty : this.staticQty;
    return input ? parseInt(input.value || '1', 10) : null;
  }

  // Helpers
  formatMoney(cents) {
    try {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, "{{ shop.money_with_currency_format | replace: '\"', '&quot;' }}");
      }
    } catch (e) {}
    const n = (cents || 0) / 100;
    try {
      return n.toLocaleString(undefined, { style: 'currency', currency: "{{ shop.currency }}" });
    } catch (e) {
      return `$${n.toFixed(2)}`;
    }
  }

  sizeImage(src, size) {
    if (!src) return src;
    return src.replace(/_(\d+)x\./, `_${size}x.`).replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, (m, ext, q = '') => `.${ext}${q}`);
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductNavBars);
} else {
  initProductNavBars();
}

function initProductNavBars() {
  const sections = document.querySelectorAll('.product-nav-bar-section');
  sections.forEach((section) => {
    new ProductNavBar(section);
  });
}

// Shopify Section Events
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.product-nav-bar-section');
    if (section) {
      new ProductNavBar(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Cleanup if needed
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.querySelector('.product-nav-bar-section');
    if (section) {
      // Scroll to the section when selected in theme editor
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}
